// ============================================
// MoltiVerse v3 - Genesis Timeline Scrubber
// ============================================
// Epic time travel interface

import { useMemo } from 'react';
import { useUniverseStore } from '@/stores/universe-store';
import { format } from 'date-fns';

export function TimelineScrubber() {
  const {
    time,
    graphData,
    setTimestamp,
    playTimelapse,
    pauseTimelapse,
    setPlaySpeed,
  } = useUniverseStore();

  const progress = useMemo(() => {
    if (time.maxTime === time.minTime) return 100;
    return ((time.currentTimestamp - time.minTime) / (time.maxTime - time.minTime)) * 100;
  }, [time.currentTimestamp, time.minTime, time.maxTime]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const percent = parseFloat(e.target.value);
    const timestamp = time.minTime + ((time.maxTime - time.minTime) * percent / 100);
    setTimestamp(timestamp);
  };

  const formatDate = (timestamp: number) => {
    try {
      return format(new Date(timestamp), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Unknown';
    }
  };

  const speeds = [
    { label: '0.5x', value: 200 },
    { label: '1x', value: 100 },
    { label: '2x', value: 50 },
    { label: '4x', value: 25 },
  ];

  return (
    <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-full max-w-4xl px-4 pointer-events-auto">
      {/* Glow behind panel */}
      <div className="absolute inset-0 bg-purple-500/10 blur-3xl" />
      
      <div className="relative bg-black/80 backdrop-blur-xl border border-purple-500/30 rounded-xl overflow-hidden">
        {/* Top accent */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/70 to-transparent" />
        
        <div className="p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  <div className="absolute inset-0 w-3 h-3 rounded-full bg-purple-500 animate-ping opacity-50" />
                </div>
                <span className="font-display text-sm font-bold text-white tracking-wider">
                  GENESIS TIME MACHINE
                </span>
              </div>
              <div className="h-4 w-px bg-white/10" />
              <span className="font-mono text-[10px] text-white/40 tracking-wide">
                Witness the birth of machine consciousness
              </span>
            </div>
            
            {/* Speed Controls */}
            <div className="flex items-center gap-2">
              <span className="font-mono text-[9px] text-white/30 tracking-widest mr-2">SPEED</span>
              {speeds.map(speed => (
                <button
                  key={speed.value}
                  onClick={() => setPlaySpeed(speed.value)}
                  className={`px-3 py-1.5 rounded-lg font-mono text-xs transition-all ${
                    time.playSpeed === speed.value
                      ? 'bg-purple-500 text-white font-bold'
                      : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70'
                  }`}
                  style={{
                    boxShadow: time.playSpeed === speed.value 
                      ? '0 0 20px rgba(168, 85, 247, 0.5)' 
                      : 'none'
                  }}
                >
                  {speed.label}
                </button>
              ))}
            </div>
          </div>

          {/* Timeline Track */}
          <div className="relative py-3">
            {/* Background track */}
            <div className="absolute top-1/2 left-0 right-0 h-2 -translate-y-1/2 bg-white/5 rounded-full overflow-hidden">
              {/* Filled progress */}
              <div 
                className="h-full rounded-full transition-all duration-100"
                style={{ 
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, rgba(168, 85, 247, 0.5), rgba(168, 85, 247, 1))',
                  boxShadow: '0 0 20px rgba(168, 85, 247, 0.5)',
                }}
              />
            </div>

            {/* Activity visualization */}
            <div className="absolute top-1/2 left-0 right-0 h-6 -translate-y-1/2 flex items-end justify-between px-1">
              {time.timeSlices.slice(0, 60).map((slice, i) => {
                const height = Math.max(4, (slice.nodeCount / 30) * 20);
                const isPast = (i / 60) * 100 <= progress;
                return (
                  <div
                    key={i}
                    className="w-1 rounded-full transition-all duration-200"
                    style={{
                      height: `${height}px`,
                      background: isPast 
                        ? `rgba(168, 85, 247, ${0.3 + (slice.nodeCount / 50) * 0.4})`
                        : `rgba(255, 255, 255, ${0.05 + (slice.nodeCount / 100) * 0.1})`,
                    }}
                  />
                );
              })}
            </div>

            {/* Slider input */}
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={progress}
              onChange={handleSliderChange}
              className="relative w-full h-8 appearance-none bg-transparent cursor-pointer z-10"
            />
            
            <style>{`
              input[type="range"]::-webkit-slider-thumb {
                -webkit-appearance: none;
                width: 20px;
                height: 20px;
                background: linear-gradient(135deg, #a855f7, #7c3aed);
                border-radius: 50%;
                cursor: pointer;
                box-shadow: 0 0 20px rgba(168, 85, 247, 0.8), 0 0 40px rgba(168, 85, 247, 0.4), inset 0 0 10px rgba(255,255,255,0.2);
                border: 2px solid rgba(255,255,255,0.5);
              }
              input[type="range"]::-webkit-slider-thumb:hover {
                transform: scale(1.1);
                box-shadow: 0 0 30px rgba(168, 85, 247, 1), 0 0 60px rgba(168, 85, 247, 0.5);
              }
              input[type="range"]::-moz-range-thumb {
                width: 20px;
                height: 20px;
                background: linear-gradient(135deg, #a855f7, #7c3aed);
                border-radius: 50%;
                cursor: pointer;
                box-shadow: 0 0 20px rgba(168, 85, 247, 0.8);
                border: 2px solid rgba(255,255,255,0.5);
              }
            `}</style>
          </div>

          {/* Bottom Controls */}
          <div className="flex items-center justify-between mt-2">
            {/* Start Time */}
            <div className="font-mono text-xs text-white/30">
              {formatDate(time.minTime)}
            </div>

            {/* Center Controls */}
            <div className="flex items-center gap-6">
              {/* Play Button */}
              <button
                onClick={time.isPlaying ? pauseTimelapse : playTimelapse}
                className="group relative w-14 h-14 rounded-full flex items-center justify-center transition-all"
                style={{
                  background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(168, 85, 247, 0.1))',
                  border: '2px solid rgba(168, 85, 247, 0.5)',
                  boxShadow: time.isPlaying 
                    ? '0 0 30px rgba(168, 85, 247, 0.5), inset 0 0 20px rgba(168, 85, 247, 0.2)'
                    : 'none',
                }}
              >
                <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" 
                  style={{ 
                    background: 'radial-gradient(circle, rgba(168, 85, 247, 0.3) 0%, transparent 70%)',
                    boxShadow: '0 0 40px rgba(168, 85, 247, 0.5)',
                  }} 
                />
                {time.isPlaying ? (
                  <svg className="w-6 h-6 text-purple-400 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="5" width="4" height="14" rx="1" />
                    <rect x="14" y="5" width="4" height="14" rx="1" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-purple-400 relative z-10 ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Current Time Display */}
              <div className="text-center min-w-[200px]">
                <div className="font-display text-xl font-bold text-white tracking-wide">
                  {formatDate(time.currentTimestamp)}
                </div>
                <div className="font-mono text-xs text-purple-400 mt-1 flex items-center justify-center gap-3">
                  <span>{graphData.nodes.length.toLocaleString()} AGENTS</span>
                  <span className="text-white/20">•</span>
                  <span>{graphData.links.length.toLocaleString()} BONDS</span>
                </div>
              </div>

              {/* Reset Button */}
              <button
                onClick={() => setTimestamp(time.minTime)}
                className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/50 hover:bg-white/10 hover:text-white/70 font-mono text-xs transition-all flex items-center gap-2"
              >
                <span>↺</span>
                <span>RESET</span>
              </button>
            </div>

            {/* End Time */}
            <div className="font-mono text-xs text-purple-400">
              {formatDate(time.maxTime)} <span className="text-white/30">(NOW)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
